/**
 * SysColect preference catalogue (`product: 'sc'`).
 *
 * Layout / grid-config keys are produced by app-kit adapters — they are not
 * listed here. Platform prefs (`themePref`, `localePref`) live in
 * `@rammetec/app-kit/preferences`.
 */
import { definePreference } from '@rammetec/data-access/preferences';
/** Last selected module instance (`mid`) for a domain slug — org scope. */
export const moduleLastMidPref = (slug) => definePreference({
    key: `module:lastSelected.${slug}`,
    product: 'sc',
    scope: 'org',
    defaultValue: null,
    parse: (raw) => (typeof raw === 'string' ? raw : null),
});
/** Last selected ticket-board workspace — org scope. */
export const ticketBoardWorkspacePref = definePreference({
    key: 'module:ticketBoard.workspace',
    product: 'sc',
    scope: 'org',
    defaultValue: null,
    parse: (raw) => (typeof raw === 'string' ? raw : null),
});
/** Default ticket-board view — org scope (`kanban` | `list`). */
export const ticketBoardViewPref = definePreference({
    key: 'module:ticketBoard.view',
    product: 'sc',
    scope: 'org',
    defaultValue: 'kanban',
    parse: (raw) => (raw === 'kanban' || raw === 'list' ? raw : null),
    label: 'Visão padrão do board',
});
/** Default printer for a print context — org scope. */
export const printDefaultPrinterPref = (printContextId) => definePreference({
    key: `print:defaultPrinter.${printContextId}`,
    product: 'sc',
    scope: 'org',
    defaultValue: null,
    parse: (raw) => (typeof raw === 'string' ? raw : null),
});
const PRODUCT_LABEL_PRINT_SOURCE_IDS = [
    'product',
    'purchase-order-item',
    'order-item',
    'production-order',
];
/** Last data source for product label print — org scope (default: produtos). */
export const productLabelPrintSourcePref = definePreference({
    key: 'module:productLabelPrint.source',
    product: 'sc',
    scope: 'org',
    defaultValue: 'product',
    parse: (raw) => typeof raw === 'string' &&
        PRODUCT_LABEL_PRINT_SOURCE_IDS.includes(raw)
        ? raw
        : null,
});
/**
 * Last template for product label print — org scope.
 * `null` ⇒ UI falls back to the first available label template.
 */
export const productLabelPrintTemplatePref = definePreference({
    key: 'module:productLabelPrint.template',
    product: 'sc',
    scope: 'org',
    defaultValue: null,
    parse: (raw) => (typeof raw === 'string' ? raw : null),
});
const VOLUME_LABEL_PRINT_SOURCE_IDS = ['handling-unit'];
/** Last data source for volume label print — org scope (only handling-unit). */
export const volumeLabelPrintSourcePref = definePreference({
    key: 'module:volumeLabelPrint.source',
    product: 'sc',
    scope: 'org',
    defaultValue: 'handling-unit',
    parse: (raw) => typeof raw === 'string' &&
        VOLUME_LABEL_PRINT_SOURCE_IDS.includes(raw)
        ? raw
        : null,
});
/**
 * Last template for volume label print — org scope.
 * `null` ⇒ UI falls back to the first available volume template.
 */
export const volumeLabelPrintTemplatePref = definePreference({
    key: 'module:volumeLabelPrint.template',
    product: 'sc',
    scope: 'org',
    defaultValue: null,
    parse: (raw) => (typeof raw === 'string' ? raw : null),
});
/**
 * Last template for production-ticket print (`producao.talao`) — org scope.
 * `null` ⇒ UI falls back to the first available ticket template.
 */
export const productionTicketPrintTemplatePref = definePreference({
    key: 'module:productionTicketPrint.template',
    product: 'sc',
    scope: 'org',
    defaultValue: null,
    parse: (raw) => (typeof raw === 'string' ? raw : null),
});
const DEFAULT_LIST_WORKBENCH_PREFS = {
    windowDays: 7,
    showCompleted: false,
};
function parseListWindowDays(raw) {
    if (raw === null || raw === 'all')
        return null;
    if (raw === 'today')
        return 'today';
    if (raw === 7 || raw === '7')
        return 7;
    if (raw === 30 || raw === '30')
        return 30;
    return undefined;
}
function parseListWorkbenchPrefs(raw) {
    if (raw == null || typeof raw !== 'object')
        return null;
    const o = raw;
    const windowDays = parseListWindowDays(o.windowDays);
    if (windowDays === undefined)
        return null;
    if (typeof o.showCompleted !== 'boolean')
        return null;
    return { windowDays, showCompleted: o.showCompleted };
}
/** Receiving workbench: period + “exibir concluídos” — user scope. */
export const receivingListPrefs = definePreference({
    key: 'module:receiving.listFilters',
    product: 'sc',
    scope: 'user',
    defaultValue: DEFAULT_LIST_WORKBENCH_PREFS,
    parse: parseListWorkbenchPrefs,
    label: 'Filtros do recebimento',
});
/**
 * Receiving workbench: “somente meus passos” — user scope, separate from
 * `receivingListPrefs` (shared parse with invoice-import — request 0225).
 */
export const receivingOnlyMyStepsPref = definePreference({
    key: 'module:receiving.onlyMySteps',
    product: 'sc',
    scope: 'user',
    defaultValue: false,
    parse: (raw) => (typeof raw === 'boolean' ? raw : null),
    label: 'Recebimento — somente meus passos',
});
/** Invoice-import workbench: period + “exibir concluídos” — user scope. */
export const invoiceImportListPrefs = definePreference({
    key: 'module:invoiceImport.listFilters',
    product: 'sc',
    scope: 'user',
    defaultValue: DEFAULT_LIST_WORKBENCH_PREFS,
    parse: parseListWorkbenchPrefs,
    label: 'Filtros da importação de NF-e',
});
const OCCUPANCY_WINDOW_PRESETS = ['journey', '3d', 'week'];
const DEFAULT_MACHINE_OCCUPANCY_PREFS = {
    windowPreset: 'journey',
    includeCancelled: false,
};
function parseMachineOccupancyListPrefs(raw) {
    if (raw == null || typeof raw !== 'object')
        return null;
    const o = raw;
    if (typeof o.windowPreset !== 'string' ||
        !OCCUPANCY_WINDOW_PRESETS.includes(o.windowPreset)) {
        return null;
    }
    if (typeof o.includeCancelled !== 'boolean')
        return null;
    return {
        windowPreset: o.windowPreset,
        includeCancelled: o.includeCancelled,
    };
}
/** Occupancy Gantt: window preset + cancelled-runs toggle — user scope. */
export const machineOccupancyListPrefs = definePreference({
    key: 'module:machineOccupancy.listFilters',
    product: 'sc',
    scope: 'user',
    defaultValue: DEFAULT_MACHINE_OCCUPANCY_PREFS,
    parse: parseMachineOccupancyListPrefs,
    label: 'Filtros da ocupação de máquinas',
});
const DEFAULT_VOLUME_RECONCILE_PREFS = {
    stockLocationId: '',
    productId: '',
};
function parseVolumeReconcileListPrefs(raw) {
    if (raw == null || typeof raw !== 'object')
        return null;
    const o = raw;
    if (typeof o.stockLocationId !== 'string' || typeof o.productId !== 'string')
        return null;
    return { stockLocationId: o.stockLocationId, productId: o.productId };
}
/** Volume reconcile workbench: last-used location / product filters — user scope. */
export const volumeReconcileListPrefs = definePreference({
    key: 'module:volumeReconcile.listFilters',
    product: 'sc',
    scope: 'user',
    defaultValue: DEFAULT_VOLUME_RECONCILE_PREFS,
    parse: parseVolumeReconcileListPrefs,
    label: 'Filtros da conciliação de volumes',
});
const DEFAULT_THIRD_PARTY_STOCK_PREFS = {
    ownerPartyId: '',
    productId: '',
    stockLocationId: '',
};
function parseThirdPartyStockListPrefs(raw) {
    if (raw == null || typeof raw !== 'object')
        return null;
    const o = raw;
    if (typeof o.ownerPartyId !== 'string' ||
        typeof o.productId !== 'string' ||
        typeof o.stockLocationId !== 'string') {
        return null;
    }
    return {
        ownerPartyId: o.ownerPartyId,
        productId: o.productId,
        stockLocationId: o.stockLocationId,
    };
}
const DEFAULT_RECONCILE_STOCK_PREFS = {
    showNonStockControl: false,
};
function parseReconcileStockListPrefs(raw) {
    if (raw == null || typeof raw !== 'object')
        return null;
    const o = raw;
    if (typeof o.showNonStockControl !== 'boolean')
        return null;
    return { showNonStockControl: o.showNonStockControl };
}
/** Invoice stock-entry reconcile dialog: show/hide non-stock lines — user scope. */
export const reconcileStockListPrefs = definePreference({
    key: 'module:invoice.reconcileStock.listFilters',
    product: 'sc',
    scope: 'user',
    defaultValue: DEFAULT_RECONCILE_STOCK_PREFS,
    parse: parseReconcileStockListPrefs,
    label: 'Filtros da entrada no estoque',
});
/** Third-party stock module (0208): last-used partner/product/location filters — user scope. */
export const thirdPartyStockListPrefs = definePreference({
    key: 'module:thirdPartyStock.listFilters',
    product: 'sc',
    scope: 'user',
    defaultValue: DEFAULT_THIRD_PARTY_STOCK_PREFS,
    parse: parseThirdPartyStockListPrefs,
    label: 'Filtros do estoque de terceiros',
});
/**
 * Assistant "Sempre aplicar" — tool names the user opted to auto-approve.
 * Only tools that declare `rememberable: true` honour entries here (app-kit).
 */
export const assistantAutoApprovePref = definePreference({
    key: 'assistant.autoApprove',
    product: 'sc',
    scope: 'user',
    defaultValue: [],
    label: 'Assistente — ações lembradas',
    parse: (raw) => {
        if (!Array.isArray(raw))
            return null;
        if (!raw.every((x) => typeof x === 'string'))
            return null;
        return raw;
    },
});
function parseImportSetupColumn(raw) {
    if (raw == null || typeof raw !== 'object')
        return null;
    const o = raw;
    if (typeof o.field !== 'string' || o.field.trim() === '')
        return null;
    if (o.matchBy !== undefined && o.matchBy !== 'code' && o.matchBy !== 'description') {
        return null;
    }
    const col = { field: o.field };
    if (o.matchBy === 'code' || o.matchBy === 'description')
        col.matchBy = o.matchBy;
    return col;
}
/** Defensive parse — invalid shape ⇒ `null` (UI falls back to default setup). */
export function parseImportSetupPref(raw) {
    if (raw == null || typeof raw !== 'object')
        return null;
    const o = raw;
    if (!Array.isArray(o.columns))
        return null;
    const columns = [];
    for (const item of o.columns) {
        const col = parseImportSetupColumn(item);
        if (!col)
            return null;
        columns.push(col);
    }
    if (o.fixed == null || typeof o.fixed !== 'object' || Array.isArray(o.fixed))
        return null;
    return {
        columns,
        fixed: { ...o.fixed },
    };
}
/**
 * Last import dialog setup for an entity — org scope (per user × org).
 * `null` ⇒ no remembered setup (default columns on open).
 */
export const importSetupPref = (entity) => definePreference({
    key: `import:setup.${entity}`,
    product: 'sc',
    scope: 'org',
    defaultValue: null,
    parse: parseImportSetupPref,
});
/**
 * Field names selected for record copy / copy-to-import — org scope.
 * `null` ⇒ all currently copyable fields (UI expands the default).
 */
export function parseCopyFieldsPref(raw) {
    if (raw == null)
        return null;
    if (!Array.isArray(raw))
        return null;
    if (!raw.every((x) => typeof x === 'string'))
        return null;
    return raw;
}
export const copyFieldsPref = (entity) => definePreference({
    key: `copy:fields.${entity}`,
    product: 'sc',
    scope: 'org',
    defaultValue: null,
    parse: parseCopyFieldsPref,
});
//# sourceMappingURL=catalogue.js.map